import { BaseEntity, Gender } from './common';
import { UserRole, UserPreferences } from './auth';

// User entity for Firestore
export interface UserEntity extends BaseEntity {
  uid: string; // Firebase Auth UID
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  photoURL?: string;
  phoneNumber?: string;
  role: UserRole;
  isApproved: boolean;
  profileComplete: boolean;
  isEmailVerified: boolean;
  personId?: string; // Link to their person record
  lastLoginAt?: Date;
  preferences: UserPreferences;
  bio?: string;
  registrationCode?: string; // Family code used during registration
}

// User list item for admin panel
export interface UserListItem {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  isApproved: boolean;
  profileComplete: boolean;
  isEmailVerified: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
  personId?: string;
}

// User statistics for admin dashboard
export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  pendingApproval: number;
  incompleteProfiles: number;
  usersByRole: Record<UserRole, number>;
  recentRegistrations: number;
}

// User activity log entry
export interface UserActivity {
  id: string;
  userId: string;
  action: UserActionType;
  target?: {
    type: 'person' | 'photo' | 'comment' | 'relationship';
    id: string;
    name?: string;
  };
  details?: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp: Date;
}

// Types of user actions for activity logging
export type UserActionType = 
  | 'login'
  | 'logout'
  | 'register'
  | 'profile_update'
  | 'password_change'
  | 'person_create'
  | 'person_update'
  | 'person_delete'
  | 'photo_upload'
  | 'photo_update'
  | 'photo_delete'
  | 'comment_create'
  | 'comment_update'
  | 'comment_delete'
  | 'relationship_create'
  | 'relationship_update'
  | 'relationship_delete'
  | 'data_export'
  | 'admin_action';

// User invitation
export interface UserInvitation {
  id: string;
  email: string;
  invitedBy: string;
  role: UserRole;
  familyCode: string;
  message?: string;
  expiresAt: Date;
  usedAt?: Date;
  createdAt: Date;
}

// User notification
export interface UserNotification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: Record<string, any>;
  read: boolean;
  actionUrl?: string;
  createdAt: Date;
  readAt?: Date;

  // Enhanced fields
  priority?: NotificationPriority;
  archived?: boolean;
  archivedAt?: Date;

  // Source tracking
  createdBy: string; // 'system' or userId
  source: 'system' | 'admin' | 'automated';

  // Grouping
  groupKey?: string; // For batching related notifications

  // Action button
  actionLabel?: string; // e.g., "View Profile"
  dismissible?: boolean; // Can user dismiss?
}

// Notification priority levels
export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// Notification types
export type NotificationType =
  // Authentication & User Management
  | 'welcome'
  | 'email_verified'
  | 'approval_granted'
  | 'approval_denied'
  | 'change_approved'
  | 'change_rejected'
  | 'role_changed'
  | 'account_suspended'
  | 'account_reactivated'
  | 'password_reset'
  | 'person_link_requested'
  | 'person_link_approved'
  | 'person_link_rejected'
  | 'person_link_removed'

  // Content Moderation
  | 'content_submitted'
  | 'content_approved'
  | 'content_rejected'
  | 'content_flagged'
  | 'edit_approved'
  | 'edit_rejected'

  // Social Interactions
  | 'comment_mention'
  | 'comment_reply'
  | 'comment_on_content'
  | 'new_comment'
  | 'photo_tagged'
  | 'relationship_suggested'

  // Family Tree Events
  | 'person_added'
  | 'person_updated'
  | 'relationship_added'
  | 'document_uploaded'
  | 'photo_uploaded'
  | 'memory_shared'
  | 'memory_added'
  | 'document_shared'

  // Research Collaboration
  | 'research_note_assigned'
  | 'research_note_updated'
  | 'research_note_completed'
  | 'research_request'
  | 'research_response'
  | 'collaboration_invite'

  // Reminders & Events
  | 'birthday_reminder'
  | 'anniversary_reminder'
  | 'death_anniversary'
  | 'task_reminder'
  | 'profile_incomplete'

  // System & Admin
  | 'system_announcement'
  | 'system_update'
  | 'maintenance_scheduled'
  | 'feature_announcement'
  | 'privacy_policy_update'
  | 'export_ready'
  | 'import_completed'
  | 'import_failed'
  | 'admin_broadcast'
  | 'custom'

  // Messaging
  | 'new_message'
  | 'message_mention'
  | 'message_reply'
  | 'message_reaction'
  | 'group_chat_added'
  | 'group_chat_removed'
  | 'group_chat_name_changed'
  | 'group_chat_member_joined'
  | 'group_chat_member_left'

  // Mentions (additional types)
  | 'note_mention'
  | 'memory_mention'
  | 'feature_request_mention';

// NotificationType enum for easier programmatic access
export const NotificationType = {
  // Authentication & User Management
  WELCOME: 'welcome' as NotificationType,
  EMAIL_VERIFIED: 'email_verified' as NotificationType,
  APPROVAL_GRANTED: 'approval_granted' as NotificationType,
  APPROVAL_DENIED: 'approval_denied' as NotificationType,
  CHANGE_APPROVED: 'change_approved' as NotificationType,
  CHANGE_REJECTED: 'change_rejected' as NotificationType,
  ROLE_CHANGED: 'role_changed' as NotificationType,
  ACCOUNT_SUSPENDED: 'account_suspended' as NotificationType,
  ACCOUNT_REACTIVATED: 'account_reactivated' as NotificationType,
  PASSWORD_RESET: 'password_reset' as NotificationType,
  ACCOUNT_APPROVED: 'approval_granted' as NotificationType, // Alias
  ACCOUNT_REJECTED: 'approval_denied' as NotificationType, // Alias

  // Content Moderation
  CONTENT_SUBMITTED: 'content_submitted' as NotificationType,
  CONTENT_APPROVED: 'content_approved' as NotificationType,
  CONTENT_REJECTED: 'content_rejected' as NotificationType,
  CONTENT_FLAGGED: 'content_flagged' as NotificationType,
  EDIT_APPROVED: 'edit_approved' as NotificationType,
  EDIT_REJECTED: 'edit_rejected' as NotificationType,

  // Social Interactions
  COMMENT_MENTION: 'comment_mention' as NotificationType,
  COMMENT_REPLY: 'comment_reply' as NotificationType,
  COMMENT_ON_CONTENT: 'comment_on_content' as NotificationType,
  NEW_COMMENT: 'new_comment' as NotificationType,
  PHOTO_TAGGED: 'photo_tagged' as NotificationType,
  RELATIONSHIP_SUGGESTED: 'relationship_suggested' as NotificationType,

  // Family Tree Events
  PERSON_ADDED: 'person_added' as NotificationType,
  PERSON_UPDATED: 'person_updated' as NotificationType,
  RELATIONSHIP_ADDED: 'relationship_added' as NotificationType,
  DOCUMENT_UPLOADED: 'document_uploaded' as NotificationType,
  PHOTO_UPLOADED: 'photo_uploaded' as NotificationType,
  MEMORY_SHARED: 'memory_shared' as NotificationType,
  MEMORY_ADDED: 'memory_added' as NotificationType,
  DOCUMENT_SHARED: 'document_shared' as NotificationType,

  // Research Collaboration
  RESEARCH_NOTE_ASSIGNED: 'research_note_assigned' as NotificationType,
  RESEARCH_NOTE_UPDATED: 'research_note_updated' as NotificationType,
  RESEARCH_NOTE_COMPLETED: 'research_note_completed' as NotificationType,
  RESEARCH_REQUEST: 'research_request' as NotificationType,
  RESEARCH_RESPONSE: 'research_response' as NotificationType,
  COLLABORATION_INVITE: 'collaboration_invite' as NotificationType,

  // Reminders & Events
  BIRTHDAY_REMINDER: 'birthday_reminder' as NotificationType,
  ANNIVERSARY_REMINDER: 'anniversary_reminder' as NotificationType,
  DEATH_ANNIVERSARY: 'death_anniversary' as NotificationType,
  TASK_REMINDER: 'task_reminder' as NotificationType,
  PROFILE_INCOMPLETE: 'profile_incomplete' as NotificationType,

  // System & Admin
  SYSTEM_ANNOUNCEMENT: 'system_announcement' as NotificationType,
  SYSTEM_UPDATE: 'system_update' as NotificationType,
  MAINTENANCE_SCHEDULED: 'maintenance_scheduled' as NotificationType,
  FEATURE_ANNOUNCEMENT: 'feature_announcement' as NotificationType,
  PRIVACY_POLICY_UPDATE: 'privacy_policy_update' as NotificationType,
  EXPORT_READY: 'export_ready' as NotificationType,
  IMPORT_COMPLETED: 'import_completed' as NotificationType,
  IMPORT_FAILED: 'import_failed' as NotificationType,
  ADMIN_BROADCAST: 'admin_broadcast' as NotificationType,
  CUSTOM: 'custom' as NotificationType,
} as const;

// Notification preferences
export interface NotificationPreferences {
  userId: string;

  // Master toggle
  enabled: boolean;
  pausedUntil?: Date;

  // Channel preferences
  channels: {
    inApp: {
      enabled: boolean;
    };
    email: {
      enabled: boolean;
      digestMode: 'immediate' | 'hourly' | 'daily' | 'weekly';
      digestTime?: string; // "09:00"
    };
  };

  // Category preferences
  categories: {
    authentication: boolean;
    contentModeration: boolean;
    socialInteractions: boolean;
    familyTreeUpdates: boolean;
    researchCollaboration: boolean;
    reminders: boolean;
    systemAnnouncements: boolean;
  };

  // Metadata
  updatedAt: Date;
  version: number;
}

// Create notification parameters
export interface CreateNotificationParams {
  userId: string;
  type: NotificationType;
  title?: string; // Auto-generated from template if omitted
  message?: string; // Auto-generated from template if omitted
  priority?: NotificationPriority;
  actionUrl?: string;
  actionLabel?: string;
  data?: Record<string, any>;
  groupKey?: string;
  dismissible?: boolean;
}

// User search result
export interface UserSearchResult {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string;
  role: UserRole;
  isApproved: boolean;
  relevanceScore: number;
}